import type { IPropertyBag } from '@deity-io/di-data-basics';
import { mapRecord } from '@deity-io/di-data-basics';
import type {
  IAddNewFieldPayload,
  IFieldPath,
  IFieldPayload,
  ISchemaState,
  ISourceTargetPath,
  IUpdateNamePayload,
  IUpdateTypePayload,
  SchemaActions
} from 'contexts/SchemaContext';
import { SchemaActionTypes } from 'contexts/SchemaContext';
import { META_KEY } from 'contracts/constants';
import type { ICollectionType, ISchema } from 'contracts/interfaces';
import * as _ from 'lodash';
import { isCollection } from 'utils/isCollection';
import type { IUpdateSettingPayload } from './SchemaContext';

const DEFAULT_NEW_FIELD_TYPE = 'string';

export const SchemaReducer = (state: ISchemaState, action: SchemaActions) => {
  // console.log(action);
  switch (action.type) {
    case SchemaActionTypes.INIT_SCHEMA:
      return {
        ...state,
        schema: action.payload?.schema as ISchema,
        uuid: action.payload.uuid,
        locked: action.payload.locked === true,
        selectedField: null,
        selectedSchema: null,
        name: action.payload.name
      };
    case SchemaActionTypes.ADD_NEW_FIELD:
      if (state.locked) {
        return state;
      }

      return {
        ...state,
        schema: addNewField(state.schema, action.payload)
      };
    case SchemaActionTypes.ADD_FIELD:
      if (state.locked) {
        return state;
      }

      return {
        ...state,
        schema: addRootField(state.schema, action.payload)
      };
    case SchemaActionTypes.REMOVE_FIELD:
      if (state.locked) {
        return state;
      }

      return {
        ...state,
        selectedField: null,
        schema: removeField(state.schema, action.payload)
      };
    case SchemaActionTypes.SWITCH_ORDER:
      if (state.locked) {
        return state;
      }

      return {
        ...state,
        selectedField: null,
        schema: orderField(state.schema, action.payload)
      };
    case SchemaActionTypes.ADD_CHILD:
      if (state.locked) {
        return state;
      }

      return {
        ...state,
        selectedField: null,
        schema: addChild(state.schema, action.payload)
      };
    case SchemaActionTypes.SELECT_FIELD:
      if (state.selectedField !== action.payload.path) {
        return {
          ...state,
          selectedField: (action.payload as IFieldPath).path,
          selectedSchema: action.payload.source
        };
      }

      return state;
    case SchemaActionTypes.UPDATE_SETTING:
      if (state.locked) {
        return state;
      }

      return updateSetting(state, action.payload);
    case SchemaActionTypes.UPDATE_NAME:
      if (state.locked) {
        return state;
      }

      return {
        ...state,
        selectedField: resetSelectedField(action.payload.path, action.payload.fieldName),
        schema: updateFieldName(state.schema, action.payload)
      };
    case SchemaActionTypes.UPDATE_TYPE:
      if (state.locked) {
        return state;
      }

      return {
        ...state,
        schema: updateFieldType(state.schema, action.payload)
      };

    default:
      return state;
  }
};

const addRootField = (state: ISchema, payload: IFieldPayload) => {
  const { path, field, val } = payload;
  const clonedState = _.cloneDeep(state);
  if (path.length === 0) {
    clonedState[field] = val as ICollectionType;

    return clonedState;
  }
  const leaf = _.get(clonedState, path.join('.'));
  if (leaf) {
    leaf[field] = val;
  }

  return clonedState;
};

const removeField = (state: ISchema, payload: IFieldPath) => {
  const { path } = payload;
  const clonedState = _.cloneDeep(state);
  if (path) {
    _.unset(clonedState, path);
  }

  return clonedState;
};

const addChild = (state: ISchema, payload: ISourceTargetPath) => {
  const { sPath, tPath } = payload;
  const clonedState = _.cloneDeep(state);
  const parent = tPath.length ? _.get(clonedState, tPath) : clonedState;
  const child = _.cloneDeep(sPath.length ? _.get(clonedState, sPath) : clonedState);
  _.unset(clonedState, sPath);
  parent[sPath[sPath.length - 1]] = child;

  return clonedState;
};

const orderField = (state: ISchema, payload: ISourceTargetPath) => {
  const { sPath, tPath } = payload;
  const clonedState = _.cloneDeep(state);
  const parentPath = [...tPath];
  parentPath.pop();

  const parentObj = parentPath.length ? _.get(clonedState, parentPath) : clonedState;
  const newField = sPath.length ? _.get(clonedState, sPath) : clonedState;
  _.unset(clonedState, sPath);
  const sourcePath = [...sPath];
  const newFieldKey = sourcePath.pop();
  const parentObjCopy = _.cloneDeep(parentObj);

  removeSiblings(clonedState, parentObj, tPath);

  if (newFieldKey && parentObj && parentObjCopy && newField) {
    insertWithOrder(parentObj, parentObjCopy, newField, newFieldKey, tPath);
  }

  return clonedState;
};

const removeSiblings = (stateTree: ISchema, parentObj: any, childPath: string[]) => {
  const parentPath = [...childPath];
  parentPath.pop();
  removeMetaKey(parentObj).forEach(k => {
    _.unset(stateTree, [...parentPath, k]);
  });
};

// const updateWithOrder = (parentObj: any, parentObjCopy: any, path: string[], newField: string, newVal: any) => {
//   removeMetaKey(parentObjCopy).forEach((k, i) => {
//     if (i === getKeyIndex(parentObjCopy, path)) {
//       parentObj[newField] = newVal;
//     } else {
//       parentObj[k] = parentObjCopy[k];
//     }
//   });
// };

const insertWithOrder = (parentObj: any, parentObjCopy: any, newObj: any, newFieldKey: string, tPath: string[]) => {
  removeMetaKey(parentObjCopy).forEach((k, i) => {
    if (i === getKeyIndex(parentObjCopy, tPath)) {
      parentObj[newFieldKey] = newObj;
      parentObj[k] = parentObjCopy[k];
    } else {
      parentObj[k] = parentObjCopy[k];
    }
  });
};

const getKeyIndex = (parentObj: any, path: string[]) =>
  removeMetaKey(parentObj).findIndex(i => i === path[path.length - 1]);

export const removeMetaKey = (obj: any) => Object.keys(obj).filter(k => k !== META_KEY);

const resetSelectedField = (path: string[], field: string) => {
  const pathCopy = [...path];
  pathCopy.pop();

  return [...pathCopy, field];
};

const updateSetting = (state: ISchemaState, payload: IUpdateSettingPayload) => {
  // const helper = new SchemaContextHelper(state);
  const field: any = payload.path.length ? _.get(state.schema, payload.path) : state.schema;
  if (field) {
    const isContainer = hasMetaField(field);
    const valuePath = isContainer ? [META_KEY, ...payload.property] : payload.property;

    const oldValue = _.get(field, valuePath);
    if (!_.isEqual(oldValue, payload.value)) {
      const stateCopy = _.cloneDeep(state);
      if (payload.value === undefined) {
        const parentPath = ['schema', ...payload.path, ...valuePath.slice(0, -1)];
        const parent = _.get(stateCopy, parentPath);
        const childKey = valuePath.pop() as string;
        if (Array.isArray(parent)) {
          const newParent = parent.splice(parseInt(childKey, 10), 1);
          // _.set(stateCopy, parentPath, newParent);
          // _.unset(stateCopy, ['schema', ...payload.path, ...valuePath]);
        } else {
          delete parent[childKey];
          // _.unset(stateCopy, ['schema', ...payload.path, ...valuePath]);
        }
      } else {
        _.set(stateCopy, ['schema', ...payload.path, ...valuePath], payload.value);
      }

      return stateCopy;
    }
  }

  return state;
};

const updateFieldName = (state: ISchema, payload: IUpdateNamePayload) => {
  const { path, fieldName } = payload;
  const clonedState = _.cloneDeep(state);
  const pathCopy = [...path];
  if (path.length) {
    const oldName = pathCopy.pop();
    const parentObj = pathCopy.length ? _.get(clonedState, pathCopy) : clonedState;
    const newParentObj: IPropertyBag = {};
    mapRecord(parentObj, (value, key) => {
      if (key === oldName) {
        newParentObj[payload.fieldName] = value;
      } else {
        newParentObj[key] = value;
      }
    });

    if (pathCopy.length) {
      _.set(clonedState, pathCopy, newParentObj);

      return clonedState;
    }

    return newParentObj as ISchema;
  }
  if (isCollection(clonedState)) {
    clonedState[META_KEY].name = payload.fieldName;
  } else {
    clonedState['name'] = payload.fieldName;
  }

  return clonedState;
};

const updateFieldType = (state: ISchema, payload: IUpdateTypePayload) => {
  const { fieldPath, type } = payload;
  const clonedState = _.cloneDeep(state);
  const fieldValue = fieldPath.length ? _.get(clonedState, fieldPath) : clonedState;

  if (_.has(fieldValue, META_KEY) && (type === 'dict' || type === 'list')) {
    _.set(fieldValue, [META_KEY], { type });
  } else {
    Object.keys(fieldValue).forEach(k => {
      _.unset(fieldValue, k);
    });
    if (type === 'dict' || type === 'list') {
      _.set(fieldValue, [META_KEY], { type });
    } else {
      _.set(fieldValue, ['type'], { [type]: {} });
    }
  }

  return clonedState;
};

const addNewField = (state: ISchema, payload: IAddNewFieldPayload) => {
  const regexp = /New_Field|New_Field_[0-9]+/i;
  const { fieldPath } = payload;
  const clonedState = _.cloneDeep(state);
  const fieldValue = fieldPath.length ? _.get(clonedState, fieldPath) : clonedState;

  let newFieldName: string = 'New_Field';
  if (_.has(fieldValue, META_KEY)) {
    const newFieldList = Object.keys(fieldValue)
      .filter(f => regexp.test(f))
      .sort((a, b) => parseInt(a.substring(10), 10) - parseInt(b.substring(10), 10));
    if (newFieldList.length) {
      const lastNewFieldIndex = _.last(newFieldList)?.substring(10);
      if (lastNewFieldIndex && _.isNumber(parseInt(lastNewFieldIndex, 10))) {
        newFieldName += `_${parseInt(lastNewFieldIndex, 10) + 1}`;
      } else {
        newFieldName += '_1';
      }
    }
    let newField: IPropertyBag;
    if ((DEFAULT_NEW_FIELD_TYPE as string) === 'dict' || (DEFAULT_NEW_FIELD_TYPE as string) === 'list') {
      newField = {};
      newField[META_KEY] = { type: DEFAULT_NEW_FIELD_TYPE };
    } else {
      newField = { type: {} };
      newField.type[DEFAULT_NEW_FIELD_TYPE] = {};
    }
    _.set(fieldValue, [newFieldName], newField);
  }

  return clonedState;
};

const hasMetaField = (field: Record<string, any>): boolean => META_KEY in field;
